import React from 'react';

import {
  Box,
  Button,
  createStyles,
  Dialog,
  DialogContent,
  IconButton,
  makeStyles,
  Typography,
  useTheme,
  useMediaQuery,
} from '@material-ui/core';
import { IoClose } from 'react-icons/io5';
import Lottie, { Options } from 'react-lottie';

import { MAIN_CONTAINER_ID } from '@vyce/core/src/constants';
import { getMobilePlatform } from '@vyce/core/src/utils';

import * as downloadAppAnimation from '@vyce/core/src/assets/animations/download-app.json';

const APP_STORE_URL = 'https://apps.apple.com/ie/app/vyce/id1664458846';
const GOOGLE_PLAY_URL = 'https://play.google.com/store/apps/details?id=com.vycemobile';

const getStoreConfig = () => {
  if (getMobilePlatform() === 'android') {
    return { url: GOOGLE_PLAY_URL, label: 'Get it on Google Play' };
  }
  return { url: APP_STORE_URL, label: 'View in App Store' };
};

const useStyles = makeStyles(theme =>
  createStyles({
    dialog: {
      '& .MuiDialog-paper': {
        width: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      },
    },
    dialogContent: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      padding: 0,
      alignItems: 'center',
      justifyContent: 'center',
    },
    closeButton: {
      position: 'absolute',
      right: 8,
      top: 8,
      zIndex: 1,
      color: theme.palette.text.secondary,
    },
    animationContainer: {
      flex: 1,
      minHeight: 0,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: theme.palette.background.paper,
    },
    content: {
      flexShrink: 0,
      padding: 24,
      paddingTop: 16,
      maxWidth: 600,
    },
    title: {
      fontWeight: 600,
      fontSize: 18,
      lineHeight: 1.4,
      textAlign: 'center',
      color: theme.palette.text.primary,
      marginBottom: 16,
    },
    description: {
      fontWeight: 500,
      fontSize: 15,
      lineHeight: 1.4,
      textAlign: 'center',
      color: theme.palette.text.secondary,
      marginBottom: 24,
    },
    primaryButton: {
      height: 56,
      borderRadius: 16,
      fontWeight: 600,
      fontSize: 16,
      marginBottom: 16,
    },
    secondaryButton: {
      height: 56,
      borderRadius: 16,
      fontWeight: 600,
      fontSize: 16,
      borderWidth: 2,
    },
  })
);

interface Props {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  showSecondaryButton?: boolean;
  showCloseButton?: boolean;
}

export const DownloadAppDialog: React.FC<Props> = ({
  open,
  onClose,
  title = 'Download Vyce App!',
  description = 'Easily manage your team members with live widgets, keep in touch via Messages, Clock In & Out of your Locations and more.',
  showSecondaryButton = true,
  showCloseButton = true,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.up('md'));
  const storeConfig = getStoreConfig();

  const animationOptions: Options = {
    loop: true,
    autoplay: true,
    animationData: (downloadAppAnimation as { default?: object }).default ?? downloadAppAnimation,
  };

  const handleViewInStore = () => {
    window.open(storeConfig.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Dialog
      fullScreen={true}
      container={document.getElementById(MAIN_CONTAINER_ID)}
      open={open}
      onClose={onClose}
      className={classes.dialog}
      aria-labelledby="download-vyce-app-dialog-title"
    >
      <DialogContent className={classes.dialogContent}>
        {showCloseButton && (
          <IconButton className={classes.closeButton} onClick={onClose} size="small" aria-label="Close">
            <IoClose size={24} />
          </IconButton>
        )}

        <Box className={classes.animationContainer}>
          <Box width="calc(100vw - 32px)" maxWidth={isTablet ? 700 : 400}>
            <Lottie options={animationOptions} height={'calc(100vh - 300px)'} width={'100%'} />
          </Box>
        </Box>

        <Box className={classes.content}>
          <Typography id="download-vyce-app-dialog-title" className={classes.title}>
            {title}
          </Typography>

          <Typography className={classes.description}>{description}</Typography>

          <Button
            fullWidth
            variant="contained"
            color="primary"
            className={classes.primaryButton}
            onClick={handleViewInStore}
          >
            {storeConfig.label}
          </Button>

          {showSecondaryButton && (
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              className={classes.secondaryButton}
              onClick={onClose}
            >
              Maybe later
            </Button>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
